/**
 * Каталог разделов и подразделов CMS — источник правды для чеклиста ролей
 * и для гейтинга (меню/роуты). Ключ раздела = чистый путь его list-роута
 * (имена ресурсов не уникальны: first-league и т.п. повторяются).
 */

export type SectionNode = {
  /** Уникальный ключ = путь роута; у контейнеров — синтетический "group:*" */
  key: string;
  label: string;
  children?: SectionNode[];
};

export const ADMIN_SECTION_ROUTE = "/admin-access";

export const SECTION_TREE: SectionNode[] = [
  { key: "/hash-tags", label: "Хэштеги" },
  { key: "/commentators", label: "Комментаторы" },
  {
    key: "group:league-info",
    label: "О лиге",
    children: [
      { key: "/league-info/about", label: "Описание лиги" },
      {
        key: "group:employees",
        label: "Сотрудники",
        children: [
          { key: "/league-info/employees/presidium", label: "Президиум" },
          { key: "/league-info/employees/team", label: "Команда" },
        ],
      },
      {
        key: "/league-info/social-responsibility",
        label: "Социальная ответственность",
      },
      { key: "/league-info/docs", label: "Документы" },
      { key: "/league-info/presentations", label: "Отчетные презентации" },
      { key: "/league-info/philosophy", label: "Философия" },
      { key: "/league-info/partner", label: "Партнерам" },
      { key: "/league-info/club-management", label: "Руководство клуба 2.0" },
      {
        key: "/league-info/investor-management",
        label: "Руководство инвестора",
      },
      {
        key: "/league-info/stadium-management",
        label: "Руководство по стадиону",
      },
      {
        key: "/league-info/lawyer-management",
        label: "Руководство юриста",
      },
      { key: "/league-info/banners", label: "Баннеры" },
    ],
  },
  {
    key: "group:matches",
    label: "Матчи",
    children: [
      { key: "/matches/first-league", label: "Первая лига" },
      { key: "/matches/second-league", label: "Вторая лига А" },
      { key: "/matches/third-league", label: "Вторая лига Б" },
    ],
  },
  { key: "/news", label: "Новости" },
  {
    key: "group:clubs",
    label: "Клубы",
    children: [
      { key: "/clubs/first-league", label: "Первая лига" },
      { key: "/clubs/second-league", label: "Вторая лига А" },
      { key: "/clubs/third-league", label: "Вторая лига Б" },
    ],
  },
  {
    key: "group:mapping",
    label: "Маппинг",
    children: [
      { key: "/mapping/pari", label: "Лига PARI" },
      { key: "/mapping/leon_a", label: "LEON A" },
      { key: "/mapping/leon_b", label: "LEON B" },
      { key: "/mapping/russtat", label: "Рустат" },
    ],
  },
  {
    key: "group:where-to-look",
    label: "Где смотреть?",
    children: [
      { key: "/where-to-look/first-league", label: "Первая лига" },
      { key: "/where-to-look/second-league", label: "Вторая лига А" },
      { key: "/where-to-look/third-league", label: "Вторая лига Б" },
      {
        key: "/where-to-look/broadcast-export",
        label: "Экспорт ссылок на матчи",
      },
    ],
  },
  { key: "/partners", label: "Партнеры" },
  { key: "/statsPushes", label: "Статистика пушей" },
  { key: "/champions-animation", label: "Анимация чемпионов" },
  { key: "/app-icon", label: "Иконка приложения" },
  { key: "/award-types", label: "Типы наград" },
  { key: "/awards", label: "Награды" },
  { key: "/medicine", label: "Медицина" },
  { key: "/support-messages", label: "Обращения" },
  { key: "/users", label: "Пользователи (моб. приложение)" },
  { key: "/usersimport", label: "Импорт пользователей" },
  { key: "/push-notification", label: "Отправка push-уведомления" },
  { key: "/segments", label: "Сегменты" },
  { key: "/video", label: "Видео" },
  {
    key: "group:tv",
    label: "ФНЛ ТВ",
    children: [
      { key: "/tv/videos", label: "Видео" },
      { key: "/tv/shows", label: "Шоу и спецпроекты" },
      { key: "/tv/collections", label: "Подборки" },
      { key: "/tv/showcase", label: "Витрина" },
      { key: "/tv/leagues", label: "Шапки лиг" },
      { key: "/tv/matches", label: "Матчи" },
      { key: "/tv/settings", label: "Настройки" },
    ],
  },
];

const collectLeafKeys = (nodes: SectionNode[]): string[] =>
  nodes.flatMap((node) =>
    node.children?.length ? collectLeafKeys(node.children) : [node.key]
  );

/** Все листовые ключи (то, что реально хранится в роли). */
export const ALL_SECTION_KEYS: string[] = collectLeafKeys(SECTION_TREE);

/** Убирает query/hash из роута ("/news?tab=1" -> "/news"). */
export const cleanRoute = (route?: string): string | undefined =>
  route ? route.split("?")[0].split("#")[0] : undefined;

/** Путь входит в раздел: точное совпадение или подпуть. */
const matchesSection = (pathname: string, sectionKey: string) =>
  pathname === sectionKey || pathname.startsWith(`${sectionKey}/`);

/** Разрешён ли текущий pathname набором доступных разделов. */
export const isPathAllowed = (pathname: string, allowed: string[]): boolean =>
  allowed.some((sectionKey) => matchesSection(pathname, sectionKey));

/**
 * Доступ к пути-контейнеру: сам раздел или любой вложенный.
 *
 * У ресурсов-контейнеров («Сотрудники», «Где смотреть?») нет своей страницы, и
 * Refine берёт путь для проверки прав из имени ресурса — получается
 * «/employees», «/where-to-look». В ролях записаны только листья
 * («/league-info/employees/presidium», «/where-to-look/first-league»),
 * поэтому кнопки на таких страницах гасли у всех, кроме супер-админа.
 */
export const isSectionOrChildAllowed = (
  pathname: string,
  allowed: string[]
): boolean =>
  isPathAllowed(pathname, allowed) ||
  allowed.some((sectionKey) => sectionKey.startsWith(`${pathname}/`));

/** Первый доступный раздел (куда редиректить с "/" и запрещённых путей). */
export const firstAllowedSection = (allowed: string[]): string | undefined =>
  ALL_SECTION_KEYS.find((key) => allowed.includes(key));
